// Admin Coupons Management
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from '../../components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus, Search, Pencil, Eye, EyeOff, Trash2, Copy, Ticket, TicketCheck,
  IndianRupee, TrendingUp, ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const PLAN_OPTIONS = ['lifetime', 'monthly', 'quarterly', 'yearly'];

const EMPTY_FORM = {
  code: '',
  description: '',
  discount_type: 'percent',
  discount_value: 20,
  max_discount_amount: '',
  min_order_amount: '',
  usage_limit: '',
  usage_limit_per_user: 1,
  valid_from: '',
  valid_until: '',
  plan_restrictions: [],
  is_active: true,
};

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const generateCode = () => {
  const prefixes = ['SAVE', 'GET', 'OFF', 'THAC', 'LEARN', 'DEAL'];
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
  const num = Math.floor(Math.random() * 90 + 10);
  return `${prefix}${num}`;
};

// datetime-local <-> ISO helpers (input needs "YYYY-MM-DDTHH:mm" in local time)
const toInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const validUntilStyle = (validUntil, status) => {
  if (status === 'expired') return 'text-destructive';
  if (!validUntil) return 'text-muted-foreground';
  const days = (new Date(validUntil) - new Date()) / 86400000;
  if (days <= 7) return 'text-orange-500';
  return '';
};

const STATUS_BADGE = {
  active: 'bg-green-500/15 text-green-600 border-green-500/30',
  inactive: 'bg-muted text-muted-foreground border-border',
  expired: 'bg-red-500/15 text-red-600 border-red-500/30',
};

export const AdminCoupons = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const [lifetimePrice, setLifetimePrice] = useState(4999);
  const [gstPercentage, setGstPercentage] = useState(18);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [usageHistory, setUsageHistory] = useState([]);
  const [usageHistoryTotal, setUsageHistoryTotal] = useState(0);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/coupons/stats`, { headers });
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter === 'active') params.is_active = 'true';
      if (statusFilter === 'inactive') params.is_active = 'false';
      if (typeFilter !== 'all') params.discount_type = typeFilter;

      const res = await axios.get(`${API_URL}/admin/coupons`, { headers, params });
      let rows = res.data.coupons || [];
      if (statusFilter === 'expired') rows = rows.filter((c) => c.status === 'expired');
      setCoupons(rows);
      setPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load coupons');
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  }, [token, page, search, statusFilter, typeFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  useEffect(() => {
    axios.get(`${API_URL}/settings`).then((res) => {
      setLifetimePrice(res.data.lifetime_price || 4999);
      setGstPercentage(res.data.gst_percentage ?? 18);
    }).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setUsageHistory([]);
    setUsageHistoryTotal(0);
    setSheetOpen(true);
  };

  const openEdit = async (coupon) => {
    setEditingId(coupon.id);
    setForm({
      code: coupon.code,
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      max_discount_amount: coupon.max_discount_amount ?? '',
      min_order_amount: coupon.min_order_amount || '',
      usage_limit: coupon.usage_limit ?? '',
      usage_limit_per_user: coupon.usage_limit_per_user ?? '',
      valid_from: toInputValue(coupon.valid_from),
      valid_until: toInputValue(coupon.valid_until),
      plan_restrictions: coupon.plan_restrictions || [],
      is_active: coupon.is_active,
    });
    setUsageHistory([]);
    setUsageHistoryTotal(0);
    setSheetOpen(true);
    try {
      const res = await axios.get(`${API_URL}/admin/coupons/${coupon.id}`, { headers });
      setUsageHistory(res.data.usage_history || []);
      setUsageHistoryTotal(res.data.usage_history_total || 0);
    } catch (err) {
      console.error(err);
    }
  };

  const togglePlan = (plan) => {
    setForm((f) => ({
      ...f,
      plan_restrictions: f.plan_restrictions.includes(plan)
        ? f.plan_restrictions.filter((p) => p !== plan)
        : [...f.plan_restrictions, plan],
    }));
  };

  const buildPayload = () => {
    const payload = {
      description: form.description,
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value) || 0,
      min_order_amount: form.min_order_amount === '' ? 0 : Number(form.min_order_amount),
      max_discount_amount: form.max_discount_amount === '' ? null : Number(form.max_discount_amount),
      usage_limit: form.usage_limit === '' ? null : parseInt(form.usage_limit, 10),
      usage_limit_per_user: form.usage_limit_per_user === '' ? null : parseInt(form.usage_limit_per_user, 10),
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
      plan_restrictions: form.plan_restrictions,
      is_active: form.is_active,
    };
    if (!editingId) payload.code = form.code.toUpperCase();
    return payload;
  };

  const validateForm = () => {
    if (!editingId && !/^[A-Z0-9-]{3,20}$/.test(form.code.toUpperCase())) {
      toast.error('Code must be 3-20 characters: letters, numbers, hyphens only');
      return false;
    }
    if (!form.discount_value || Number(form.discount_value) <= 0) {
      toast.error('Discount value must be greater than 0');
      return false;
    }
    if (form.discount_type === 'percent' && Number(form.discount_value) > 100) {
      toast.error('Percent discount cannot exceed 100');
      return false;
    }
    if (form.valid_from && form.valid_until && form.valid_until <= form.valid_from) {
      toast.error('Valid until must be after valid from');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await axios.put(`${API_URL}/admin/coupons/${editingId}`, payload, { headers });
        toast.success('Coupon updated');
      } else {
        await axios.post(`${API_URL}/admin/coupons`, payload, { headers });
        toast.success('Coupon created');
      }
      setSheetOpen(false);
      fetchCoupons();
      fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (coupon) => {
    setToggling(coupon.id);
    try {
      const res = await axios.post(`${API_URL}/admin/coupons/${coupon.id}/toggle`, {}, { headers });
      setCoupons((cs) => cs.map((c) => (c.id === coupon.id ? { ...c, is_active: res.data.is_active, status: res.data.is_active ? 'active' : 'inactive' } : c)));
      fetchStats();
    } catch (err) {
      toast.error('Failed to toggle coupon');
    } finally {
      setToggling(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/admin/coupons/${deleteTarget.id}`, { headers });
      toast.success('Coupon deleted');
      setDeleteTarget(null);
      fetchCoupons();
      fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete coupon');
    } finally {
      setDeleting(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied!');
  };

  // Cosmetic live preview only — real checkout applies the discount to the
  // GST-inclusive price (see routes/payments.py::_lifetime_charge_amount),
  // not the base price. This illustrates the discount breakdown against the
  // base price for admin-facing clarity.
  const preview = (() => {
    const base = lifetimePrice;
    const value = Number(form.discount_value) || 0;
    let discount = form.discount_type === 'fixed' ? value : base * (value / 100);
    let capped = false;
    const cap = form.max_discount_amount === '' ? null : Number(form.max_discount_amount);
    if (form.discount_type === 'percent' && cap !== null && discount > cap) {
      discount = cap;
      capped = true;
    }
    discount = Math.min(discount, base);
    const afterDiscount = Math.max(0, base - discount);
    const gstAmount = afterDiscount * (gstPercentage / 100);
    const total = afterDiscount + gstAmount;
    return { base, discount, capped, afterDiscount, gstAmount, total };
  })();

  return (
    <div data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-serif font-medium">Coupons</h1>
          <p className="text-muted-foreground">Create and manage discount coupons</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Create Coupon
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><Ticket className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.total_coupons ?? '—'}</p>
              <p className="text-xs text-muted-foreground">Total Coupons</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center"><TicketCheck className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.active_coupons ?? '—'}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold">{stats?.total_uses ?? '—'}</p>
              <p className="text-xs text-muted-foreground">Total Uses</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center"><IndianRupee className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-2xl font-bold">{money(stats?.total_discount_given)}</p>
              <p className="text-xs text-muted-foreground">Total Saved</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search coupons..."
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            className="pl-9 w-64"
          />
        </div>
        <Select value={typeFilter} onValueChange={(v) => { setPage(1); setTypeFilter(v); }}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="percent">Percent</SelectItem>
            <SelectItem value="fixed">Fixed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setPage(1); setStatusFilter(v); }}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Valid Until</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : coupons.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No coupons found</TableCell></TableRow>
              ) : coupons.map((coupon) => {
                const usagePct = coupon.usage_limit ? Math.min(100, (coupon.usage_count / coupon.usage_limit) * 100) : 0;
                return (
                  <TableRow key={coupon.id} className="cursor-pointer" onClick={() => openEdit(coupon)}>
                    <TableCell>
                      <button
                        type="button"
                        className="font-mono font-bold flex items-center gap-1.5 hover:text-primary"
                        onClick={(e) => { e.stopPropagation(); copyCode(coupon.code); }}
                        title="Click to copy"
                      >
                        {coupon.code}
                        <Copy className="w-3 h-3 opacity-50" />
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={coupon.discount_type === 'percent' ? 'bg-purple-500/10 text-purple-600 border-purple-500/30' : 'bg-green-500/10 text-green-600 border-green-500/30'}>
                        {coupon.discount_type === 'percent' ? '%' : '₹'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {coupon.discount_type === 'percent' ? `${coupon.discount_value}%` : money(coupon.discount_value)}
                    </TableCell>
                    <TableCell>
                      <div className="w-28">
                        <span className="text-sm">{coupon.usage_count} / {coupon.usage_limit ?? '∞'}</span>
                        {coupon.usage_limit ? <Progress value={usagePct} className="h-1 mt-1" /> : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={validUntilStyle(coupon.valid_until, coupon.status)}>
                        {coupon.valid_until ? new Date(coupon.valid_until).toLocaleDateString() : 'No expiry'}
                        {coupon.status === 'expired' && ' (Expired)'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_BADGE[coupon.status]}>
                        {coupon.status.charAt(0).toUpperCase() + coupon.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(coupon)} title="Edit">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleToggle(coupon)} disabled={toggling === coupon.id} title="Toggle active">
                          {coupon.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(coupon)} title="Delete">
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">{total} coupon{total === 1 ? '' : 's'} total</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="w-4 h-4" /> Prev
            </Button>
            <span className="text-sm">Page {page} of {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Drawer */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingId ? 'Edit Coupon' : 'Create Coupon'}</SheetTitle>
          </SheetHeader>

          <div className="space-y-5 mt-6">
            <div>
              <Label>Coupon Code</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="SAVE20"
                  disabled={!!editingId}
                  className="font-mono"
                />
                {!editingId && (
                  <Button type="button" variant="outline" onClick={() => setForm((f) => ({ ...f, code: generateCode() }))}>
                    Generate Random
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {editingId ? 'Code cannot be changed after creation' : 'Code will be auto-uppercased'}
              </p>
            </div>

            <div>
              <Label>Description (internal note)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="For new users — social campaign"
                className="mt-1.5"
              />
            </div>

            <Separator />

            <div>
              <p className="text-sm font-medium mb-3">Discount</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percent</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Value {form.discount_type === 'percent' ? '(%)' : '(₹)'}</Label>
                  <Input
                    type="number" min="0"
                    value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div className="mt-3">
                <Label>Max Discount Amount (optional)</Label>
                <Input
                  type="number" min="0"
                  value={form.max_discount_amount}
                  onChange={(e) => setForm({ ...form, max_discount_amount: e.target.value })}
                  placeholder="500"
                  className="mt-1.5"
                />
                <p className="text-xs text-muted-foreground mt-1">Cap the discount, e.g. ₹500 (applies to percent coupons)</p>
              </div>

              <div className="mt-3">
                <Label>Minimum Order Amount</Label>
                <Input
                  type="number" min="0"
                  value={form.min_order_amount}
                  onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
                  placeholder="0"
                  className="mt-1.5"
                />
              </div>
            </div>

            <Separator />

            <div>
              <p className="text-sm font-medium mb-3">Usage Limits</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Total Uses Allowed</Label>
                  <Input
                    type="number" min="1"
                    value={form.usage_limit}
                    onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
                    placeholder="Leave blank = ∞"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Uses Per User</Label>
                  <Input
                    type="number" min="1"
                    value={form.usage_limit_per_user}
                    onChange={(e) => setForm({ ...form, usage_limit_per_user: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <p className="text-sm font-medium mb-3">Validity</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Valid From</Label>
                  <Input
                    type="datetime-local"
                    value={form.valid_from}
                    onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Valid Until</Label>
                  <Input
                    type="datetime-local"
                    value={form.valid_until}
                    onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Leave blank = active immediately / never expires</p>
            </div>

            <Separator />

            <div>
              <p className="text-sm font-medium mb-3">Plan Restrictions</p>
              <div className="flex flex-wrap gap-2">
                {PLAN_OPTIONS.map((plan) => (
                  <Button
                    key={plan}
                    type="button"
                    size="sm"
                    variant={form.plan_restrictions.includes(plan) ? 'default' : 'outline'}
                    onClick={() => togglePlan(plan)}
                  >
                    {plan.charAt(0).toUpperCase() + plan.slice(1)}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">None selected = applies to all plans</p>
            </div>

            <Separator />

            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="text-sm font-medium mb-2">Preview (Lifetime plan)</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Original price</span><span>{money(preview.base)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>-{money(preview.discount)}</span></div>
                {preview.capped && (
                  <div className="flex justify-between text-xs"><span className="text-muted-foreground">Max cap applied</span><span></span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">After discount</span><span>{money(preview.afterDiscount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">+{gstPercentage}% GST</span><span>{money(preview.gstAmount)}</span></div>
                <Separator className="my-1" />
                <div className="flex justify-between font-semibold"><span>Total</span><span>{money(preview.total)}</span></div>
              </div>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <Label>Status</Label>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <span className="text-sm">{form.is_active ? 'Active' : 'Inactive'}</span>
              </div>
            </div>

            {editingId && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-medium mb-3">Recent Uses</p>
                  {usageHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No uses yet</p>
                  ) : (
                    <div className="space-y-2">
                      {usageHistory.map((u) => (
                        <div key={u.id} className="flex justify-between text-sm">
                          <span>{u.user_name}</span>
                          <span className="text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</span>
                          <span className="text-destructive">-{money(u.discount_amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {usageHistoryTotal > usageHistory.length && (
                    <p className="text-xs text-muted-foreground mt-2">Showing latest {usageHistory.length} of {usageHistoryTotal} uses</p>
                  )}
                </div>
              </>
            )}
          </div>

          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Coupon
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Coupon "{deleteTarget?.code}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This coupon has been used {deleteTarget?.usage_count || 0} times.
              Deleting will deactivate it — existing payment records are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete Coupon
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
